/**
 * LOS LECTORES DE CARTA DE ELEGIBILIDAD QUE NO SON EL FEDERAL.
 *
 * Vivian dentro de crm-app/components/ClientDetail.jsx, donde no se pueden
 * probar (importar el componente arrastra api.js -> AuthGate.jsx -> MSAL). Se
 * mudaron aqui el 2026-10-22 (E-379) para probarlos con textos inventados, sin
 * PHI, igual que crm-app/documentosDeElegibilidad.js.
 *
 * Lissette, 2026-10-22: *"me estan reportando errores cuando se suben
 * elegibilidad: colocan solicitando documentos que no dicen en el documento"*.
 * Dos de los fallos estaban aqui:
 *
 *   - PENNIE: para cada "(Not Verified)" se miraban los 45 caracteres de antes
 *     buscando el tipo, y esa ventana llegaba a la FILA ANTERIOR:
 *     "Income (Verified) Residency (Not Verified)" pedia INGRESOS, que ya
 *     estaban verificados, en vez de residencia.
 *   - MARYLAND: se elegia con solo ver "Additional Information Required". Si no
 *     encontraba su tabla, miraba los ultimos 1200 caracteres antes del anexo y
 *     encendia cualquier tipo cuya palabra saliera ahi (/ingreso|income/,
 *     /residen/…) — y volvia en el acto, sin pasar por ninguna de las
 *     protecciones del lector general.
 *
 * El resto se mudo tal cual.
 */
import { fechaDeCarta, limpiarCarta } from './documentosDeElegibilidad.js';

// ── Verification type → alert mapping (bilingual) ──────────────────────────
// Shared by every marketplace parser. Each verification label found in a PDF
// (Spanish or English, federal or state) is classified into one canonical
// document-request alert. Order matters: more specific rules go first.
export const VERIFICATION_TYPES = [
  { rx: /presencia legal|lawful presence|inmigraci|immigration|estatus migratorio|migratori/i,
    type: 'immigration_docs', doc: 'Documentos de Estatus Migratorio', docEn: 'Immigration Status Documents' },
  { rx: /ingreso|income/i,
    type: 'income_docs',      doc: 'Verificación de Ingresos',         docEn: 'Income Verification' },
  { rx: /ciudadan|citizenship/i,
    type: 'citizenship_docs', doc: 'Prueba de Ciudadanía',             docEn: 'Citizenship Proof' },
  { rx: /identidad|\bidentity\b/i,
    type: 'identity_docs',    doc: 'Prueba de Identidad',              docEn: 'Identity Proof' },
  { rx: /seguridad social|social security/i,
    type: 'ssn_docs',         doc: 'Número de Seguro Social',          docEn: 'Social Security Number' },
  { rx: /residen/i,
    type: 'residency_docs',   doc: 'Prueba de Residencia',             docEn: 'Residency Proof' },
];
export function classifyVerification(label) {
  for (const t of VERIFICATION_TYPES) if (t.rx.test(label)) return t;
  return { type: 'generic_docs', doc: 'Documento Requerido', docEn: 'Required Document' };
}

// Build an alert object with the person name appended to the label, matching
// the style the federal parser uses ("<doc> — <person>").
function buildStateAlert(cls, personName, deadline) {
  const suffix = personName ? ' — ' + personName : '';
  return {
    type: cls.type,
    doc: cls.doc + suffix,
    docEn: cls.docEn + suffix,
    persons: personName ? [personName] : [],
    deadline: deadline || '',
    status: 'pending',
  };
}

// La marca de una fila de Pennie, verificada o no. Sirve para cortar la
// ventana de "lo que hay antes": lo que queda detras de la marca anterior es
// OTRA fila (E-379).
const MARCA_DE_FILA_PENNIE = /(?:No\s*Verificad[oa]|Not\s*Verified|Verificad[oa]|Verified)\s*\)?/gi;

/**
 * Lo que hay justo antes de una marca "(Not Verified)", sin cruzar la marca de
 * la fila anterior. Exportada para la prueba.
 */
export function etiquetaAntesDeLaMarca(flat, indice) {
  const back = flat.slice(Math.max(0, indice - 45), indice);
  let corte = 0;
  let m;
  MARCA_DE_FILA_PENNIE.lastIndex = 0;
  while ((m = MARCA_DE_FILA_PENNIE.exec(back))) corte = m.index + m[0].length;
  return back.slice(corte);
}

// ── Pennie (Pennsylvania) marketplace ──────────────────────────────────────
// Layout: "Verificaciones del solicitante" / "Applicant Verifications" with a
// list of items each marked "(No Verificado)"/"(Not Verified)" (needs docs) or
// "(Verificado)"/"(Verified)" (done). Deadline is per-item ("...antes de
// 09/24/2026" / "Action needed by 09/24/2026") with an app-level fallback
// ("a más tardar el 09-24-2026" / "by 09-24-2026").
export function parsePennieFormat(flat) {
  // Detection tolerant to OCR: parentheses/spacing may vary, so don't require
  // the exact "(No Verificado)" form here.
  const isPennie =
    /(Verificaciones del solicitante|Applicant Verifications)/i.test(flat) &&
    /(?:No\s*Verificad[oa]|Not\s*Verified|Verificad[oa]|Verified)/i.test(flat);
  if (!isPennie) return null;

  // Applicant name sits between the tab title and the intro paragraph.
  let clientNameFromPdf = '';
  const nm = flat.match(
    /(?:Verificaciones del solicitante|Applicant Verifications)\s+([A-Za-zÀ-ÿ'’.\s]+?)\s+(?:No pudimos|We weren'?t|We were not)/i,
  );
  if (nm) clientNameFromPdf = nm[1].replace(/\s+/g, ' ').trim();
  // Virginia imprime la página web y el nombre sale en la barra de arriba
  // ("Viewing Individual Account (Nombre)"), no entre el título y el párrafo (E-412).
  if (!clientNameFromPdf) {
    const va = flat.match(/Viewing Individual Account\s*\(([^)]{3,80})\)/i);
    if (va) clientNameFromPdf = va[1].replace(/\s+/g, ' ').trim();
  }

  // App-level fallback deadline from the intro paragraph.
  let topDeadline = '';
  const td = flat.match(/(?:a m[áa]s tardar el|by)\s+(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/i);
  if (td) topDeadline = fechaDeCarta(td[1]) || '';

  const alerts = [];
  const seen = new Set();
  // Match each "Not Verified" / "No Verificado" marker (parentheses optional,
  // spacing flexible — OCR of a photo mangles both). For each marker, look
  // BACK ~45 chars for a known verification keyword (Income, Presencia legal,
  // etc.) and classify. Requiring a NON-generic classification means the intro
  // paragraph ("...indicated below as Not Verified") is ignored, because no
  // verification keyword sits right before it.
  // E-379: la ventana se corta en la marca de la fila anterior; si no, en
  // "Income (Verified) Residency (Not Verified)" ganaba "Income", que va
  // primero en VERIFICATION_TYPES, y se pedian unos ingresos ya verificados.
  const re = /\(?\s*(?:No\s*Verificad[oa]|Not\s*Verified)\s*\)?/gi;
  let m;
  while ((m = re.exec(flat))) {
    const back = etiquetaAntesDeLaMarca(flat, m.index);
    const cls = classifyVerification(back);
    if (cls.type === 'generic_docs') continue; // no keyword nearby → intro text
    if (seen.has(cls.type)) continue;
    seen.add(cls.type);
    // Per-item deadline: scan the text immediately after the marker.
    const tail = flat.slice(re.lastIndex, re.lastIndex + 90);
    let dl = '';
    const dm = tail.match(
      /(?:antes del|antes de|needed by|required by|by|before)\s+(?:el\s+)?(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4}|\d{1,2}\s+de\s+\w+\s+de\s+\d{4}|\w+\s+\d{1,2},?\s+\d{4})/i,
    );
    if (dm) dl = fechaDeCarta(dm[1]) || '';
    alerts.push(buildStateAlert(cls, clientNameFromPdf, dl || topDeadline));
  }
  return { clientNameFromPdf, applicationId: '', alerts, resultsOnly: alerts.length === 0 };
}

// ── Maryland Health Connection ─────────────────────────────────────────────
// Layout: English letter "Additional Information Required Reminder" with a
// table (Name | Program | Verification | Due Date | Result If not Verified).
// The table cells wrap and interleave in flat text, so we detect verification
// keywords inside the table region and take the first MM/DD/YYYY as the due
// date.
//
// E-379: SIN LA TABLA, null. Antes miraba los ultimos 1200 caracteres antes del
// anexo y pedia cualquier tipo cuya palabra saliera ahi —"income" sale en
// cualquier parrafo—. Devolver null deja la carta al lector general, que solo
// cuenta el sitio donde la carta pide algo; si ahi tampoco hay peticion, no se
// inventa ninguna.
export function parseMarylandFormat(flat) {
  const isMD =
    /Maryland Health Connection/i.test(flat) ||
    /Additional (?:Information|Verification) Required/i.test(flat);
  if (!isMD) return null;

  // Isolate the verification table so we don't pick up the generic
  // "Acceptable Documents" catalog (which lists every possible type).
  const tbl = flat.match(
    /Result\s+If\s+not\s+Verified([^]*?)(?:We strongly encourage|ACCEPTABLE DOCUMENTS|Acceptable Documents)/i,
  );
  if (!tbl) return null;
  const region = tbl[1];

  const appIdMatch = flat.match(/Application ID\s*:?\s*(\d+)/i);
  const applicationId = appIdMatch ? appIdMatch[1] : '';

  // Recipient name: ALL-CAPS words before a street address. An optional
  // MM/DD/YYYY (the letter date) can sit between the name and the street
  // number in the flattened text, so allow one.
  let clientNameFromPdf = '';
  const nm = flat.match(
    /([A-ZÀ-Ÿ][A-ZÀ-Ÿ'.\-]+(?:\s+[A-ZÀ-Ÿ][A-ZÀ-Ÿ'.\-]+){1,3})\s+(?:\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4}\s+)?\d{3,6}\s+[A-Z][a-z]/,
  );
  if (nm) clientNameFromPdf = nm[1].replace(/\s+/g, ' ').trim();

  // Due date = first MM/DD/YYYY in the table region (due column precedes the
  // "coverage will end" result column).
  let deadline = '';
  const dd = region.match(/(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/);
  if (dd) deadline = fechaDeCarta(dd[1]) || '';

  const alerts = [];
  const seen = new Set();
  for (const t of VERIFICATION_TYPES) {
    if (t.rx.test(region) && !seen.has(t.type)) {
      seen.add(t.type);
      alerts.push(buildStateAlert(t, clientNameFromPdf, deadline));
    }
  }
  return { clientNameFromPdf, applicationId, alerts, resultsOnly: alerts.length === 0 };
}

// ── Results-only summaries (Virginia, etc.) ────────────────────────────────
// "Mis resultados de elegibilidad" / "Eligibility Overview": informational
// APTC/eligibility results with NO pending documents. We detect these so the
// UI can show a clear "saved, nothing pending" message instead of a scary
// "no documents detected" error.
// Federal notices always bundle a generic multi-page "Eligibility Guide"
// appendix AFTER the actual per-application results/DMI pages — it's
// identical boilerplate on every notice regardless of this application's
// status (e.g. "If the Marketplace asks you to 'immediately' submit
// documents to confirm a loss of health coverage..." describes a
// hypothetical Special Enrollment scenario, not a real pending item).
// Keyword-matching that boilerplate produced a false "needs documents" on
// a clean English "Eligible to enroll" notice with zero pending rows
// (Lissette 2026-07-30). Strip it before any needs-documents detection —
// the guide carries no per-application data worth extracting.
export function stripGuideAppendix(flat) {
  const m = flat.match(/(?:Welcome to the\s+)?MARKETPLACE ELIGIBILITY GUIDE|GU[ÍI]A DE ELEGIBILIDAD DEL MERCADO/i);
  return m ? flat.slice(0, m.index) : flat;
}

export function isResultsOnlyEligibility(flat) {
  const resultsFlat = stripGuideAppendix(flat);
  // "Aviso de Elegibilidad del Mercado" + "resultados de su elegibilidad":
  // the federal 2026 Spanish notice (Lissette 2026-07-04, LUIS ALBORNOZ) —
  // pure results, no pending docs, but with none of the older titles.
  const looksResults =
    /(Resumen de elegibilidad|resultados de (?:su )?elegibilidad|Eligibility (?:Results|Overview)|Elegible para lo siguiente|Eligible for the following|Aviso de Elegibilidad del Mercado|Marketplace Eligibility Notice)/i.test(resultsFlat);
  // Markers of an ACTUAL pending-documents section. Deliberately narrow:
  // federal notices carry generic FAQ boilerplate ("es posible que deba
  // enviar documentos para confirmar su elegibilidad para algunos Períodos
  // Especiales...", "si su agencia estatal necesita que envíe documentos
  // para confirmar la información") that must NOT count — a real DMI letter
  // always says "El Mercado necesita documentos" / "presente documentos
  // para confirmar X" / "(No Verificado)".
  //
  // E-379: se mira la carta LIMPIA (sin condicionales ni la enumeracion
  // generica), y "Documents to confirm" tiene que ir seguido de un tipo. La
  // frase del folleto "submit documents to confirm your income, citizenship,
  // immigration status…" bastaba para que una carta de resultados dejara de
  // serlo, y entonces el lector general le abria un aviso generico.
  const limpio = limpiarCarta(resultsFlat);
  const needsDocs =
    /(No Verificad|Not Verified|necesita documentos|needs documents|Additional (?:Information|Verification) Required|presente documentos para|Documents to confirm\s+(?:your\s+)?(?:U\.?\s?S\.?\s+)?(?:immigration|citizenship|income|identity|social security|lawful)|env[íi]e documentos antes|send documents by|Documentos para confirmar (?:el |su )?(?:estatus|ciudadan|inmigraci|ingres|identidad))/i.test(limpio);
  return looksResults && !needsDocs;
}

// ── Virginia: la página de RESULTADOS con "Acción necesaria" (E-412) ────────
//
// La pantalla "Mis resultados de elegibilidad" de Virginia's Insurance
// Marketplace NO dice qué documento falta: por cada persona pone "Acción
// necesaria · Documentos requeridos · Tiene 90 días para enviar la
// documentación requerida". Antes se leía como "resumen sin documentos
// pendientes" (✅), que es justo lo contrario. Aquí se abre un aviso genérico
// por cada persona que lo tenga, con la fecha de la solicitud + esos días; el
// detalle (qué documento) está en la página "Documents for This Household",
// que lee parsePennieFormat.
const ELEGIBLE_PARA = /(?:Elegible para lo siguiente|Eligible for the following)/gi;
const PIDE_DOCUMENTOS = /(?:Acci[óo]n necesaria|Action (?:needed|required))\s+(?:Documentos requeridos|Documents? (?:required|needed))/i;

function nombreAntesDe(flat, indice) {
  const palabras = flat.slice(Math.max(0, indice - 120), indice).trim().split(/\s+/);
  const nombre = [];
  for (let i = palabras.length - 1; i >= 0 && nombre.length < 6; i--) {
    const w = palabras[i];
    if (/^[A-ZÁÉÍÓÚÑÜ][A-Za-zÀ-ÿ'’.-]*$/.test(w)) nombre.unshift(w);
    else break;
  }
  return nombre.length >= 2 ? nombre.join(' ') : '';
}

function sumarDias(iso, dias) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m || !dias) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function parseResultadosConAccionNecesaria(flat) {
  if (!PIDE_DOCUMENTOS.test(flat)) return null;
  const marcas = [];
  let m;
  ELEGIBLE_PARA.lastIndex = 0;
  while ((m = ELEGIBLE_PARA.exec(flat))) marcas.push(m.index);
  if (!marcas.length) return null;

  const solicitud = flat.match(/(?:Fecha de solicitud|Application date)\s*:?\s*([A-Za-zÀ-ÿ]+\s+\d{1,2},?\s+\d{4}|\d{1,2}\s+de\s+\w+\s+de\s+\d{4}|\d{1,2}[\/-]\d{1,2}[\/-]\d{4})/i);
  const dias = Number((flat.match(/(?:Tiene|You have)\s+(\d{1,3})\s+(?:d[ií]as|days)/i) || [])[1]) || 0;
  const plazo = sumarDias(solicitud ? fechaDeCarta(solicitud[1]) : '', dias);
  const appId = (flat.match(/(?:ID de solicitud|Application ID)\s*:?\s*(\d+)/i) || [])[1] || '';

  const alerts = [];
  marcas.forEach((ini, i) => {
    const fin = i + 1 < marcas.length ? marcas[i + 1] : flat.length;
    if (!PIDE_DOCUMENTOS.test(flat.slice(ini, fin))) return;
    const persona = nombreAntesDe(flat, ini);
    alerts.push(buildStateAlert(classifyVerification(''), persona, plazo));
  });
  if (!alerts.length) return null;
  return { clientNameFromPdf: '', applicationId: appId, alerts, resultsOnly: false };
}
